// src/hooks/useProducts.ts - Hooks pour la gestion des produits

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  writeBatch,
  deleteField,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { Product, InventoryFilters } from '../types/inventory';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { useAuth } from '../contexts/AuthContext';
import { useCreateStockNotification } from './useNotifications';
import {
  checkBarcodeAvailability,
  findProductByBarcode,
  generateUniqueInternalBarcode,
} from '../services/barcodeService';
import { usePermissions } from './usePermissions';
import { useDefaultPointOfSaleSite } from './useSites';
import { getStockAlertThresholdForSite } from '../utils/stockAlerts';
import { getMissingDefaultSiteError, resolveDefaultSalesOrStockSite } from '../utils/siteDefaults';
import { nameSimilarityScore } from '../features/super-admin/utils/sourceProductMatch';
import type { Business } from '../types/business';
import type { Site } from '../types/site';
import { getProductDisplayName } from '../utils/productPackaging';
import { AppError } from '../utils/i18n/AppError';

const FIRESTORE_BATCH_SIZE = 500;

/** Supprime tous les documents d'une requête (par lots de 500). */
async function deleteAllDocsFromQuery(
  q: ReturnType<typeof query>,
  errorLabel: string
): Promise<void> {
  const snapshot = await getDocs(q).catch((error) => {
    handleFirestoreError(error, errorLabel);
    throw error;
  });
  const docs = snapshot.docs;
  for (let i = 0; i < docs.length; i += FIRESTORE_BATCH_SIZE) {
    const slice = docs.slice(i, i + FIRESTORE_BATCH_SIZE);
    const batch = writeBatch(db);
    slice.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
}

/** Ventes produits + mouvements de stock liés à un produit (avant suppression du document produit). */
export async function deleteProductLinkedRecords(
  businessId: string,
  productId: string
): Promise<void> {
  await deleteAllDocsFromQuery(
    query(
      collection(db, COLLECTIONS.productSales),
      where('businessId', '==', businessId),
      where('productId', '==', productId)
    ),
    'deleteProductLinkedRecords - productSales'
  );
  await deleteAllDocsFromQuery(
    query(
      collection(db, COLLECTIONS.stockMovements),
      where('businessId', '==', businessId),
      where('productId', '==', productId)
    ),
    'deleteProductLinkedRecords - stockMovements'
  );
  await deleteAllDocsFromQuery(
    query(
      collection(db, COLLECTIONS.notifications),
      where('businessId', '==', businessId),
      where('productId', '==', productId)
    ),
    'deleteProductLinkedRecords - notifications'
  );
}

/** Supprime toutes les alertes stock (rupture / faible) du business — utile si le catalogue est vide ou après purge. */
export async function deleteAllStockAlertNotificationsForBusiness(
  businessId: string
): Promise<void> {
  for (const type of ['low_stock', 'out_of_stock'] as const) {
    await deleteAllDocsFromQuery(
      query(
        collection(db, COLLECTIONS.notifications),
        where('businessId', '==', businessId),
        where('type', '==', type)
      ),
      `deleteAllStockAlertNotificationsForBusiness - ${type}`
    );
  }
}

// ===== HOOKS POUR LES PRODUITS =====

export const useProducts = (filters?: InventoryFilters) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['inventory', 'products', businessId, filters],
    queryFn: async () => {
      if (!businessId) {
        console.warn('Aucun businessId disponible');
        return [];
      }

      try {
        let q = query(
          collection(db, COLLECTIONS.products),
          where('businessId', '==', businessId),
          orderBy('name', 'asc')
        );

        // Filtre par catégorie côté serveur (Firebase)
        if (filters?.categoryId) {
          q = query(q, where('categoryId', '==', filters.categoryId));
        }

        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useProducts - Main query');
          throw error;
        });
        
        let products = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            businessId: data.businessId,
            name: data.name,
            categoryId: data.categoryId,
            categoryName: data.categoryName,
            barcode: data.barcode,
            sellingPrice: data.sellingPrice,
            costPrice: data.costPrice,
            minStockAlert: data.minStockAlert,
            minStockAlertBySite: data.minStockAlertBySite,
            currentStock: data.currentStock,
            stockBySite: data.stockBySite,
            description: data.description,
            brand: data.brand,
            packagingValue: data.packagingValue,
            packagingUnit: data.packagingUnit,
            isActive: data.isActive !== false,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            // ✅ CORRIGER : Ajouter les champs TVA manquants
            taxRateId: data.taxRateId,
            priceIncludesTax: data.priceIncludesTax,
            // ✅ NOUVEAU : Quota référent
            quotaPercentage: data.quotaPercentage,
          } as Product;
        });

        // Filtres côté client (SEULEMENT lowStock et outOfStock)
        const isLowStockProduct = (product: Product) => {
          const stockBySite = product.stockBySite || {};
          const siteEntries = Object.entries(stockBySite);
          if (siteEntries.length > 0) {
            return siteEntries.some(([siteId, qty]) => {
              const safeQty = Number(qty) || 0;
              const threshold = getStockAlertThresholdForSite(product, siteId);
              return safeQty > 0 && safeQty <= threshold;
            });
          }
          const currentStock = Number(product.currentStock || 0);
          const threshold = Number(product.minStockAlert || 0);
          return currentStock > 0 && currentStock <= threshold;
        };

        const isOutOfStockProduct = (product: Product) => {
          const stockBySite = product.stockBySite || {};
          const siteEntries = Object.entries(stockBySite);
          if (siteEntries.length > 0) {
            return siteEntries.every(([, qty]) => (Number(qty) || 0) <= 0);
          }
          return Number(product.currentStock || 0) === 0;
        };

        if (filters?.lowStock) {
          products = products.filter((product) => isLowStockProduct(product));
        }

        if (filters?.outOfStock) {
          products = products.filter((product) => isOutOfStockProduct(product));
        }

        // PAS de filtrage searchTerm ici - c'est fait dans le composant

        return products;
        
      } catch (error) {
        console.error('Erreur lors de la récupération des produits:', error);
        return [];
      }
    },
    enabled:
      !!businessId &&
      (!!permissions?.canViewProducts ||
        !!permissions?.canViewInventoryReports ||
        !!permissions?.canViewProductSales ||
        !!permissions?.canCreateProductSales ||
        !!permissions?.canViewDashboard ||
        !!permissions?.canViewAccounting ||
        !!permissions?.canViewCashReports),
  });
};

export const useProduct = (productId: string, enabled: boolean) => {
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['inventory', 'products', businessId, productId],
    queryFn: async () => {
      if (!productId || !businessId) return null;

      const docRef = doc(db, COLLECTIONS.products, productId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new AppError('products.notFound');
      }

      const data = docSnap.data();
      
      // Vérifier l'appartenance au business
      if (data.businessId !== businessId) {
        throw new AppError('products.wrongBusiness');
      }

      return {
        id: docSnap.id,
        businessId: data.businessId,
        name: data.name,
        categoryId: data.categoryId,
        categoryName: data.categoryName,
        barcode: data.barcode,
        sellingPrice: data.sellingPrice,
        costPrice: data.costPrice,
        minStockAlert: data.minStockAlert,
        minStockAlertBySite: data.minStockAlertBySite,
        currentStock: data.currentStock,
        stockBySite: data.stockBySite,
        description: data.description,
        brand: data.brand,
        packagingValue: data.packagingValue,
        packagingUnit: data.packagingUnit,
        isActive: data.isActive,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        taxRateId: data.taxRateId,
        priceIncludesTax: data.priceIncludesTax,
        // ✅ NOUVEAU : Quota référent
        quotaPercentage: data.quotaPercentage,
      } as Product;
    },
    enabled: !!productId && !!businessId && enabled,
  });
};

export const useAddProduct = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, isSuperAdmin } = useAuth();
  const businessId = currentBusiness?.id;
  const defaultPosSite = useDefaultPointOfSaleSite(businessId);
  const canSetProductStock = isSuperAdmin;
  
  return useMutation({
    mutationFn: async (productData: Omit<Product, 'id' | 'businessId' | 'createdAt' | 'updatedAt'>) => {
      if (!businessId) {
        throw new AppError('businessUndefined');
      }
      if (!defaultPosSite?.id) {
        throw new Error(getMissingDefaultSiteError(currentBusiness));
      }

      // Vérifier que la catégorie existe et appartient au business
      const categoryRef = doc(db, COLLECTIONS.productCategories, productData.categoryId);
      const categorySnap = await getDoc(categoryRef);
      
      if (!categorySnap.exists()) {
        throw new AppError('products.categoryNotFound');
      }
      
      const categoryData = categorySnap.data();
      if (categoryData.businessId !== businessId) {
        throw new AppError('products.categoryWrongBusiness');
      }

      // ✅ NOUVEAU : Vérifier l'unicité du nom (insensible à la casse)
      const normalizedName = productData.name.trim().toLowerCase();
      const nameQuery = query(
        collection(db, COLLECTIONS.products),
        where('businessId', '==', businessId),
        where('isActive', '==', true)
      );
      
      const nameSnap = await getDocs(nameQuery).catch(error => {
        handleFirestoreError(error, 'useAddProduct - Name uniqueness check');
        throw error;
      });
      
      const duplicateName = nameSnap.docs.some(doc => {
        const existingName = doc.data().name?.toLowerCase() || '';
        return existingName === normalizedName;
      });
      
      if (duplicateName) {
        throw new AppError('products.duplicateName');
      }

      // Vérifier l'unicité du code-barres s'il est fourni
      if (productData.barcode) {
        const barcodeQuery = query(
          collection(db, COLLECTIONS.products),
          where('businessId', '==', businessId),
          where('barcode', '==', productData.barcode.trim()),
          where('isActive', '==', true)
        );
        
        const barcodeSnap = await getDocs(barcodeQuery).catch(error => {
          handleFirestoreError(error, 'useCheckBarcodeAvailability - Barcode query');
          throw error;
        });
        if (!barcodeSnap.empty) {
          throw new AppError('products.duplicateBarcode');
        }
      }

      // Quantités : Super Admin uniquement. Espace client → création à 0 (appro / mouvements).
      const normalizedStockBySite = canSetProductStock
        ? productData.stockBySite && Object.keys(productData.stockBySite).length > 0
          ? Object.entries(productData.stockBySite).reduce((acc, [siteId, qty]) => {
              const safeQty = Number(qty);
              acc[siteId] = Number.isFinite(safeQty) ? Math.max(0, safeQty) : 0;
              return acc;
            }, {} as Record<string, number>)
          : {
              [defaultPosSite.id]: Math.max(0, Number(productData.currentStock ?? 0)),
            }
        : productData.stockBySite && Object.keys(productData.stockBySite).length > 0
          ? Object.keys(productData.stockBySite).reduce((acc, siteId) => {
              acc[siteId] = 0;
              return acc;
            }, {} as Record<string, number>)
          : {
              [defaultPosSite.id]: 0,
            };
      const normalizedMinStockAlertBySite =
        productData.minStockAlertBySite && Object.keys(productData.minStockAlertBySite).length > 0
          ? Object.entries(productData.minStockAlertBySite).reduce((acc, [siteId, qty]) => {
              const safeQty = Number(qty);
              acc[siteId] = Number.isFinite(safeQty) ? Math.max(0, safeQty) : 0;
              return acc;
            }, {} as Record<string, number>)
          : undefined;

      const totalStock = Object.values(normalizedStockBySite).reduce((sum, qty) => sum + qty, 0);

      const newProduct: any = {
        businessId,
        categoryId: productData.categoryId,
        categoryName: categoryData.name,
        name: productData.name.trim(),
        minStockAlert: productData.minStockAlert,
        ...(normalizedMinStockAlertBySite ? { minStockAlertBySite: normalizedMinStockAlertBySite } : {}),
        currentStock: totalStock,
        stockBySite: normalizedStockBySite,
        isActive: productData.isActive,
        createdAt: Timestamp.now(),
        taxRateId: productData.taxRateId,
        priceIncludesTax: productData.priceIncludesTax,
        // ✅ NOUVEAU : Quota référent (optionnel)
        ...(productData.quotaPercentage !== undefined && { quotaPercentage: productData.quotaPercentage }),
      };
      
      // ✅ MODIFIÉ : Ajouter les prix seulement s'ils sont définis
      if (productData.sellingPrice !== undefined && productData.sellingPrice !== null) {
        newProduct.sellingPrice = productData.sellingPrice;
      }
      
      if (productData.costPrice !== undefined && productData.costPrice !== null) {
        newProduct.costPrice = productData.costPrice;
      }
      
      // Ajouter conditionnellement les champs optionnels
      if (productData.barcode?.trim()) {
        newProduct.barcode = productData.barcode.trim();
      }
      
      if (productData.brand?.trim()) {
        newProduct.brand = productData.brand.trim();
      }
      
      if (productData.description?.trim()) {
        newProduct.description = productData.description.trim();
      }

      if (productData.packagingValue !== undefined && productData.packagingUnit) {
        newProduct.packagingValue = productData.packagingValue;
        newProduct.packagingUnit = productData.packagingUnit;
      }

      if (productData.barcode) {
        const finalCheck = await checkBarcodeAvailability(
          productData.barcode, 
          businessId
        );
        if (!finalCheck.isAvailable) {
          throw new AppError('products.barcodeRace');
        }
      }

      const docRef = await addDoc(collection(db, COLLECTIONS.products), newProduct);
      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
    },
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, isSuperAdmin } = useAuth();
const businessId = currentBusiness?.id;
  const canSetProductStock = isSuperAdmin;
  
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Product> & { id: string }) => {
      if (!businessId) {
        throw new AppError('businessUndefined');
      }

      // Vérifier que le produit appartient au business
      const productRef = doc(db, COLLECTIONS.products, id);
      const productSnap = await getDoc(productRef);
      
      if (!productSnap.exists()) {
        throw new AppError('products.notFound');
      }
      
      const existingProduct = productSnap.data();
      if (existingProduct.businessId !== businessId) {
        throw new AppError('products.wrongBusiness');
      }

      const updateData: any = {
        updatedAt: Timestamp.now(),
      };

      // ✅ NOUVEAU : Vérifier l'unicité du nom si il change (insensible à la casse)
      if (data.name && data.name.trim().toLowerCase() !== existingProduct.name?.toLowerCase()) {
        const normalizedName = data.name.trim().toLowerCase();
        const nameQuery = query(
          collection(db, COLLECTIONS.products),
          where('businessId', '==', businessId),
          where('isActive', '==', true)
        );
        
        const nameSnap = await getDocs(nameQuery).catch(error => {
          handleFirestoreError(error, 'useUpdateProduct - Name uniqueness check');
          throw error;
        });
        
        const duplicateName = nameSnap.docs.some(doc => {
          // Exclure le produit actuel de la vérification
          if (doc.id === id) return false;
          const existingName = doc.data().name?.toLowerCase() || '';
          return existingName === normalizedName;
        });
        
        if (duplicateName) {
          throw new AppError('products.duplicateName');
        }
      }

      // Vérifier la catégorie si elle change
      if (data.categoryId && data.categoryId !== existingProduct.categoryId) {
        const categoryRef = doc(db, COLLECTIONS.productCategories, data.categoryId);
        const categorySnap = await getDoc(categoryRef);
        
        if (!categorySnap.exists()) {
          throw new AppError('products.categoryNotFound');
        }
        
        const categoryData = categorySnap.data();
        if (categoryData.businessId !== businessId) {
          throw new AppError('products.categoryWrongBusiness');
        }
        
        updateData.categoryId = data.categoryId;
        updateData.categoryName = categoryData.name;
      }

      // Vérifier l'unicité du code-barres s'il change
      if (data.barcode && data.barcode !== existingProduct.barcode) {
        const barcodeQuery = query(
          collection(db, COLLECTIONS.products),
          where('businessId', '==', businessId),
          where('barcode', '==', data.barcode.trim()),
          where('isActive', '==', true)
        );
        
        const barcodeSnap = await getDocs(barcodeQuery).catch(error => {
          handleFirestoreError(error, 'useCheckBarcodeAvailability - Barcode query');
          throw error;
        });
        const duplicateExists = barcodeSnap.docs.some(doc => doc.id !== id);
        
        if (duplicateExists) {
          throw new AppError('products.duplicateBarcode');
        }
        
        updateData.barcode = data.barcode.trim();
      }

      // Autres champs modifiables
      if (data.name) updateData.name = data.name.trim();
      
      // ✅ CORRIGÉ : Mettre à jour les prix même s'ils sont 0 ou null
      // On vérifie explicitement la présence de la clé dans l'objet data
      if ('sellingPrice' in data) {
        updateData.sellingPrice = data.sellingPrice === null ? null : data.sellingPrice;
      }
      if ('costPrice' in data) {
        updateData.costPrice = data.costPrice === null ? null : data.costPrice;
      }
      
      // Stock quantitatif : Super Admin uniquement (sinon approvisionnements / mouvements)
      if (canSetProductStock && data.currentStock !== undefined) {
        updateData.currentStock = Math.max(0, data.currentStock); // Empêcher le stock négatif
      }
      if (canSetProductStock && data.stockBySite && typeof data.stockBySite === 'object') {
        const normalizedStockBySite = Object.entries(data.stockBySite).reduce((acc, [siteId, qty]) => {
          const safeQty = Number(qty);
          acc[siteId] = Number.isFinite(safeQty) ? Math.max(0, safeQty) : 0;
          return acc;
        }, {} as Record<string, number>);

        updateData.stockBySite = normalizedStockBySite;
        if (data.currentStock === undefined) {
          updateData.currentStock = Object.values(normalizedStockBySite).reduce((sum, qty) => sum + qty, 0);
        }
      }
      if (data.minStockAlert !== undefined) {
        updateData.minStockAlert = Math.max(0, data.minStockAlert); // Empêcher l'alerte négative
      }
      if (data.minStockAlertBySite && typeof data.minStockAlertBySite === 'object') {
        const normalizedMinStockAlertBySite = Object.entries(data.minStockAlertBySite).reduce((acc, [siteId, qty]) => {
          const safeQty = Number(qty);
          acc[siteId] = Number.isFinite(safeQty) ? Math.max(0, safeQty) : 0;
          return acc;
        }, {} as Record<string, number>);
        updateData.minStockAlertBySite = normalizedMinStockAlertBySite;
      }
      
      if (data.brand !== undefined) updateData.brand = data.brand?.trim() || undefined;
      if (data.description !== undefined) updateData.description = data.description?.trim() || undefined;
      if (data.isActive !== undefined) updateData.isActive = data.isActive;
      
      // ✅ CORRIGER : Ajouter les champs TVA manquants
      if (data.taxRateId !== undefined) updateData.taxRateId = data.taxRateId;
      if (data.priceIncludesTax !== undefined) updateData.priceIncludesTax = data.priceIncludesTax;
      
      // ✅ NOUVEAU : Quota référent (optionnel)
      if (data.quotaPercentage !== undefined) {
        updateData.quotaPercentage = data.quotaPercentage === null ? null : data.quotaPercentage;
      }

      if ('packagingValue' in data) {
        if (
          data.packagingValue === undefined ||
          data.packagingValue === null ||
          !data.packagingUnit
        ) {
          updateData.packagingValue = deleteField();
          updateData.packagingUnit = deleteField();
        } else {
          updateData.packagingValue = data.packagingValue;
          updateData.packagingUnit = data.packagingUnit;
        }
      }

      await updateDoc(productRef, updateData);
      return { id, ...data };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
    },
  });
};

export const useUpdateProductStock = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, isSuperAdmin, isCSM } = useAuth();
  const businessId = currentBusiness?.id;
  const defaultPosSite = useDefaultPointOfSaleSite(businessId);
  const createStockNotification = useCreateStockNotification();
  
  return useMutation({
    mutationFn: async ({ productId, newStock }: { productId: string; newStock: number }) => {
      if (!businessId) {
        throw new AppError('businessUndefined');
      }
      if (!isSuperAdmin && !isCSM) {
        throw new AppError('superAdminOrCsmOnly');
      }

      const productRef = doc(db, COLLECTIONS.products, productId);
      const productSnap = await getDoc(productRef);
      
      if (!productSnap.exists()) {
        throw new AppError('products.notFound');
      }
      
      const productData = productSnap.data();
      if (productData.businessId !== businessId) {
        throw new AppError('products.wrongBusiness');
      }

      const finalStock = Math.max(0, newStock); // Empêcher le stock négatif
      const stockBySite = (productData.stockBySite as Record<string, number>) || {};
      const hasStockBySite = Object.keys(stockBySite).length > 0;
      const resolvedSiteId = defaultPosSite?.id;

      // En mode multi-sites, ce hook ajuste le stock du POS par défaut
      // puis recalcule le stock global comme somme des lieux.
      let updatedStockBySite: Record<string, number> | undefined;
      let finalGlobalStock = finalStock;
      if (resolvedSiteId) {
        updatedStockBySite = {
          ...stockBySite,
          [resolvedSiteId]: finalStock,
        };
        finalGlobalStock = Object.values(updatedStockBySite).reduce(
          (sum, qty) => sum + (Number(qty) || 0),
          0
        );
      } else if (hasStockBySite) {
        finalGlobalStock = Object.values(stockBySite).reduce(
          (sum, qty) => sum + (Number(qty) || 0),
          0
        );
      }
      const minStockAlert = getStockAlertThresholdForSite(productData, resolvedSiteId);

      await updateDoc(productRef, {
        currentStock: Math.max(0, finalGlobalStock),
        ...(updatedStockBySite ? { stockBySite: updatedStockBySite } : {}),
        updatedAt: Timestamp.now(),
      });

      // Synchroniser l'état de l'alerte de stock
      try {
        await createStockNotification.mutateAsync({
          productId,
          productName: getProductDisplayName(productData),
          currentStock: finalStock,
          minStockAlert,
          ...(resolvedSiteId ? { siteId: resolvedSiteId, siteName: defaultPosSite?.name } : {}),
        });
      } catch (error) {
        console.warn('Erreur lors de la synchronisation de la notification de stock:', error);
        // Ne pas faire échouer la mutation si la notification échoue
      }

      return { productId, newStock: finalStock, currentStock: Math.max(0, finalGlobalStock) };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
    },
  });
};


// src/hooks/useProducts.ts - Ajout du hook pour recherche par code-barres

// Ajouter ce hook à la fin du fichier useProducts.ts existant

export const useFindProductByBarcode = () => {
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async (barcode: string) => {
      if (!businessId || !barcode) {
        throw new AppError('products.barcodeMissing');
      }

      const product = await findProductByBarcode(barcode, businessId);
      
      if (!product) {
        throw new AppError('products.barcodeNotFound');
      }

      return product;
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (productId: string) => {
      if (!businessId) {
        throw new AppError('businessUndefined');
      }

      // Vérifier que le produit appartient au business
      const productRef = doc(db, COLLECTIONS.products, productId);
      const productSnap = await getDoc(productRef);
      
      if (!productSnap.exists()) {
        throw new AppError('products.notFound');
      }
      
      const productData = productSnap.data();
      if (productData.businessId !== businessId) {
        throw new AppError('products.wrongBusiness');
      }

      await deleteProductLinkedRecords(businessId, productId);
      await deleteDoc(productRef);

      return productId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'productSales', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', businessId] });
      queryClient.invalidateQueries({ queryKey: ['productSales', 'check', businessId] });
    },
  });
};

export const useProductHasSales = (productId?: string) => {
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['productSales', 'check', businessId, productId],
    queryFn: async () => {
      if (!businessId || !productId) {
        return false;
      }

      const productSalesQuery = query(
        collection(db, COLLECTIONS.productSales),
        where('businessId', '==', businessId),
        where('productId', '==', productId),
        limit(1) // On a juste besoin de savoir s'il y en a au moins un
      );
      
      const productSalesSnap = await getDocs(productSalesQuery).catch(error => {
        handleFirestoreError(error, 'useProductSales - ProductSales query');
        throw error;
      });
      return !productSalesSnap.empty;
    },
    enabled: !!businessId && !!productId,
  });
};

/** Liste des produits actifs d'un business (export / import Super Admin). */
export const useProductsForBusinessId = (
  businessId: string | undefined,
  options?: { enabled?: boolean }
) => {
  const enabled = options?.enabled !== false;
  return useQuery({
    queryKey: ['inventory', 'products', 'forBusiness', businessId],
    queryFn: async () => {
      if (!businessId) return [];

      const q = query(
        collection(db, COLLECTIONS.products),
        where('businessId', '==', businessId),
        where('isActive', '==', true),
        orderBy('name', 'asc')
      );

      const snapshot = await getDocs(q).catch((error) => {
        handleFirestoreError(error, 'useProductsForBusinessId');
        throw error;
      });

      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          businessId: data.businessId,
          name: data.name,
          categoryId: data.categoryId,
          categoryName: data.categoryName,
          barcode: data.barcode,
          sellingPrice: data.sellingPrice,
          costPrice: data.costPrice,
          minStockAlert: data.minStockAlert,
          currentStock: data.currentStock,
          description: data.description,
          brand: data.brand,
          packagingValue: data.packagingValue,
          packagingUnit: data.packagingUnit,
          isActive: data.isActive,
        } as Product;
      });
    },
    enabled: !!businessId && enabled,
  });
};

export type SuperAdminImportProductRow = {
  name: string;
  categoryId: string;
  barcode?: string;
  sellingPrice?: number;
  costPrice?: number;
  minStockAlert: number;
  currentStock: number;
  brand?: string;
  description?: string;
  packagingValue?: number;
  packagingUnit?: string;
  /** Produit boutique source choisi manuellement (rapprochement flou). Sinon appariement auto code-barres + nom identique. */
  sourceProductId?: string;
};

export type BulkImportRowError = {
  rowIndex: number;
  name: string;
  message: string;
};

export type BulkImportProgressEvent =
  | { kind: 'start'; total: number }
  | { kind: 'row'; index: number; total: number; name: string }
  | { kind: 'success'; index: number; total: number; name: string; productId: string }
  | { kind: 'fail'; index: number; total: number; name: string; message: string }
  | { kind: 'done'; created: number; errors: BulkImportRowError[] };

export type SuperAdminBulkImportResult = {
  created: number;
  ids: string[];
  errors: BulkImportRowError[];
};

type BulkImportAuth = {
  isSuperAdmin: boolean;
  isCSM: boolean;
  userBusinessIds: string[] | undefined;
};

type SourceProductLookup = {
  byId: Map<string, DocumentData>;
  candidates: { id: string; name: string; data: DocumentData }[];
};

function assertBulkImportBusinessAccess(
  auth: BulkImportAuth,
  targetBusinessId: string
): void {
  const { isSuperAdmin, isCSM, userBusinessIds } = auth;
  if (!isSuperAdmin && !(isCSM && (userBusinessIds?.includes(targetBusinessId) ?? false))) {
    throw new AppError('accessDenied');
  }
}

async function buildSourceProductLookup(sourceBusinessId: string): Promise<SourceProductLookup> {
  const byId = new Map<string, DocumentData>();
  const candidates: { id: string; name: string; data: DocumentData }[] = [];
  const snap = await getDocs(
    query(
      collection(db, COLLECTIONS.products),
      where('businessId', '==', sourceBusinessId),
      where('isActive', '==', true)
    )
  ).catch((e) => {
    handleFirestoreError(e, 'buildSourceProductLookup');
    throw e;
  });
  for (const d of snap.docs) {
    const data = d.data();
    byId.set(d.id, data);
    const name = String(data.name ?? '').trim();
    if (name) {
      candidates.push({ id: d.id, name, data });
    }
  }
  return { byId, candidates };
}

function findMatchingSourceProduct(
  row: SuperAdminImportProductRow,
  lookup: SourceProductLookup
): DocumentData | null {
  const MIN_SIMILARITY = 0.6;
  const manualId = row.sourceProductId?.trim();
  if (manualId) {
    const doc = lookup.byId.get(manualId);
    if (!doc) {
      throw new Error(
        'Rapprochement produit source invalide : ce produit n’existe pas dans la boutique source (ou a été désactivé).'
      );
    }
    return doc;
  }
  const importName = String(row.name ?? '').trim();
  if (!importName) return null;

  // Match fuzzy par nom (Levenshtein normalisé). On prend le meilleur candidat ≥ 60%.
  let best: { similarity: number; data: DocumentData } | null = null;
  for (const c of lookup.candidates) {
    const similarity = nameSimilarityScore(importName, c.name);
    if (similarity < MIN_SIMILARITY) continue;
    if (!best || similarity > best.similarity) {
      best = { similarity, data: c.data };
    }
  }
  return best?.data ?? null;
}

function numFromSource(data: DocumentData, key: string): number | undefined {
  const v = data[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return undefined;
}

function strFromSource(data: DocumentData, key: string): string | undefined {
  const v = data[key];
  if (typeof v === 'string') {
    const t = v.trim();
    return t || undefined;
  }
  return undefined;
}

/** Complète les champs absents de la ligne avec le produit source (même nom ou même code-barres dans la boutique source). */
export function mergeImportRowWithSource(
  row: SuperAdminImportProductRow,
  source: DocumentData | null
): SuperAdminImportProductRow {
  if (!source) return row;
  return {
    ...row,
    sellingPrice: row.sellingPrice ?? numFromSource(source, 'sellingPrice'),
    costPrice: row.costPrice ?? numFromSource(source, 'costPrice'),
    brand: row.brand?.trim() || strFromSource(source, 'brand'),
    description: row.description?.trim() || strFromSource(source, 'description'),
  };
}

/**
 * Création en masse avec progression et erreurs par ligne (les lignes en erreur n’arrêtent pas les suivantes).
 */
export async function runSuperAdminBulkImportProducts(
  params: {
    businessId: string;
    rows: SuperAdminImportProductRow[];
    auth: BulkImportAuth;
    /** Boutique dont on copie prix / marque / description pour les cellules vides (appariement par code-barres puis par nom). */
    sourceBusinessId?: string;
    onProgress?: (e: BulkImportProgressEvent) => void;
  }
): Promise<SuperAdminBulkImportResult> {
  const { businessId, rows, auth, onProgress, sourceBusinessId } = params;
  const { isSuperAdmin, isCSM, userBusinessIds } = auth;

  assertBulkImportBusinessAccess(auth, businessId);

  let sourceLookup: SourceProductLookup | null = null;
  if (sourceBusinessId?.trim()) {
    const srcId = sourceBusinessId.trim();
    if (srcId === businessId) {
      throw new AppError('products.importSameShop');
    }
    assertBulkImportBusinessAccess(auth, srcId);
    sourceLookup = await buildSourceProductLookup(srcId);
  }
  if (!rows.length) {
    throw new AppError('products.importEmpty');
  }

  const sitesSnap = await getDocs(
    query(collection(db, COLLECTIONS.sites), where('businessId', '==', businessId))
  ).catch((e) => {
    handleFirestoreError(e, 'useSuperAdminBulkImportProducts sites');
    throw e;
  });

  const allSites = sitesSnap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      businessId,
      name: String(data.name ?? ''),
      type: data.type === 'point_of_sale' ? 'point_of_sale' : 'warehouse',
      isActive: data.isActive !== false,
      createdAt: data.createdAt?.toDate?.() ?? new Date(),
    } as Site;
  });

  const businessRef = doc(db, COLLECTIONS.businesses, businessId);
  const businessSnap = await getDoc(businessRef).catch((e) => {
    handleFirestoreError(e, 'useSuperAdminBulkImportProducts business');
    throw e;
  });
  if (!businessSnap.exists()) {
    throw new AppError('products.shopNotFound');
  }
  const businessData = businessSnap.data();
  const business = {
    id: businessSnap.id,
    ...businessData,
    configuration: businessData.configuration,
    activeFeatures: businessData.activeFeatures,
    disabledFeatures: businessData.disabledFeatures,
  } as Business;

  const defaultPosSite = resolveDefaultSalesOrStockSite(allSites, business);
  if (!defaultPosSite?.id) {
    throw new Error(getMissingDefaultSiteError(business));
  }

  const businessType = String(businessData?.type ?? 'beauty_salon');

  const createdIds: string[] = [];
  const errors: BulkImportRowError[] = [];

  const existingProductsSnap = await getDocs(
    query(
      collection(db, COLLECTIONS.products),
      where('businessId', '==', businessId),
      where('isActive', '==', true)
    )
  ).catch((error) => {
    handleFirestoreError(error, 'useSuperAdminBulkImportProducts existing products');
    throw error;
  });

  const usedNames = new Set<string>();
  const usedBarcodes = new Set<string>();
  existingProductsSnap.docs.forEach((d) => {
    const data = d.data();
    if (data.name) usedNames.add(String(data.name).toLowerCase());
    if (data.barcode) usedBarcodes.add(String(data.barcode).trim().toLowerCase());
  });

  onProgress?.({ kind: 'start', total: rows.length });

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const displayName = row.name?.trim() || `Ligne ${i + 1}`;

    onProgress?.({ kind: 'row', index: i, total: rows.length, name: displayName });

    try {
      const srcDoc =
        sourceLookup !== null ? findMatchingSourceProduct(row, sourceLookup) : null;
      const effectiveRow = mergeImportRowWithSource(row, srcDoc);

      const categoryRef = doc(db, COLLECTIONS.productCategories, effectiveRow.categoryId);
      const categorySnap = await getDoc(categoryRef);
      if (!categorySnap.exists()) {
        throw new AppError('products.categoryNotFound');
      }
      const categoryData = categorySnap.data();
      if (categoryData.businessId !== businessId) {
        throw new AppError('products.categoryInvalid');
      }

      const normalizedName = effectiveRow.name.trim().toLowerCase();
      if (usedNames.has(normalizedName)) {
        throw new Error(
          `Un produit avec le nom « ${effectiveRow.name} » existe déjà (ou doublon dans l’import).`
        );
      }

      let resolvedBarcode: string | undefined = effectiveRow.barcode?.trim() || undefined;

      if (resolvedBarcode) {
        const bcl = resolvedBarcode.toLowerCase();
        if (usedBarcodes.has(bcl)) {
          throw new Error(`Code-barres déjà utilisé (${resolvedBarcode}).`);
        }
        const finalCheck = await checkBarcodeAvailability(resolvedBarcode, businessId);
        if (!finalCheck.isAvailable) {
          throw new Error(`Code-barres indisponible (${resolvedBarcode}).`);
        }
      } else {
        const generated = await generateUniqueInternalBarcode(
          businessId,
          businessType,
          String(categoryData.name ?? '')
        );
        resolvedBarcode = generated.barcode;
        const gcl = resolvedBarcode.toLowerCase();
        if (usedBarcodes.has(gcl)) {
          throw new Error(
            `Conflit de code-barres généré (${resolvedBarcode}). Réessayez l’import.`
          );
        }
      }

      const minStockAlert = Math.max(0, Math.round(Number(effectiveRow.minStockAlert) || 0));
      const currentStock = Math.max(0, Math.round(Number(effectiveRow.currentStock) || 0));

      const normalizedStockBySite: Record<string, number> = {
        [defaultPosSite.id]: currentStock,
      };
      const totalStock = currentStock;

      const productData: Omit<Product, 'id' | 'businessId' | 'createdAt' | 'updatedAt'> = {
        name: effectiveRow.name.trim(),
        categoryId: effectiveRow.categoryId,
        categoryName: categoryData.name,
        minStockAlert,
        currentStock: totalStock,
        stockBySite: normalizedStockBySite,
        isActive: true,
        sellingPrice: effectiveRow.sellingPrice,
        costPrice: effectiveRow.costPrice,
        barcode: resolvedBarcode,
        brand: effectiveRow.brand?.trim() || undefined,
        description: effectiveRow.description?.trim() || undefined,
        packagingValue: effectiveRow.packagingValue,
        packagingUnit: effectiveRow.packagingUnit,
      };

      const newProduct: Record<string, unknown> = {
        businessId,
        categoryId: productData.categoryId,
        categoryName: productData.categoryName,
        name: productData.name,
        minStockAlert: productData.minStockAlert,
        currentStock: productData.currentStock,
        stockBySite: productData.stockBySite,
        isActive: productData.isActive,
        createdAt: Timestamp.now(),
      };

      if (productData.sellingPrice !== undefined && productData.sellingPrice !== null) {
        newProduct.sellingPrice = productData.sellingPrice;
      }
      if (productData.costPrice !== undefined && productData.costPrice !== null) {
        newProduct.costPrice = productData.costPrice;
      }
      newProduct.barcode = productData.barcode;
      if (productData.brand) {
        newProduct.brand = productData.brand;
      }
      if (productData.description) {
        newProduct.description = productData.description;
      }
      if (productData.packagingValue !== undefined && productData.packagingUnit) {
        newProduct.packagingValue = productData.packagingValue;
        newProduct.packagingUnit = productData.packagingUnit;
      }

      const docRef = await addDoc(collection(db, COLLECTIONS.products), newProduct);
      createdIds.push(docRef.id);
      usedNames.add(normalizedName);
      if (resolvedBarcode) {
        usedBarcodes.add(resolvedBarcode.toLowerCase());
      }

      onProgress?.({
        kind: 'success',
        index: i,
        total: rows.length,
        name: displayName,
        productId: docRef.id,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      errors.push({ rowIndex: i, name: displayName, message });
      onProgress?.({
        kind: 'fail',
        index: i,
        total: rows.length,
        name: displayName,
        message,
      });
    }
  }

  onProgress?.({ kind: 'done', created: createdIds.length, errors });

  return { created: createdIds.length, ids: createdIds, errors };
}

/** Création en masse de produits pour un business cible (route admin). */
export const useSuperAdminBulkImportProducts = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM, userProfile } = useAuth();

  return useMutation({
    mutationFn: async (params: {
      businessId: string;
      rows: SuperAdminImportProductRow[];
      sourceBusinessId?: string;
      onProgress?: (e: BulkImportProgressEvent) => void;
    }) => {
      const { businessId, rows, onProgress, sourceBusinessId } = params;
      return runSuperAdminBulkImportProducts({
        businessId,
        rows,
        sourceBusinessId,
        auth: {
          isSuperAdmin,
          isCSM,
          userBusinessIds: userProfile?.businessIds,
        },
        onProgress,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', 'forBusiness', variables.businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', variables.businessId] });
    },
  });
};

/** Suppression de tous les produits du business (ventes + mouvements de stock liés supprimés en cascade). Réservé au Super Admin. */
export const useSuperAdminPurgeAllProductsForBusiness = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();

  return useMutation({
    mutationFn: async (params: { businessId: string }) => {
      if (!isSuperAdmin) {
        throw new AppError('superAdminOnly');
      }
      const { businessId } = params;

      const productsSnap = await getDocs(
        query(collection(db, COLLECTIONS.products), where('businessId', '==', businessId))
      ).catch((error) => {
        handleFirestoreError(error, 'useSuperAdminPurgeAllProductsForBusiness list');
        throw error;
      });

      let deleted = 0;

      for (const d of productsSnap.docs) {
        const productId = d.id;
        await deleteProductLinkedRecords(businessId, productId);
        await deleteDoc(doc(db, COLLECTIONS.products, productId));
        deleted += 1;
      }

      await deleteAllStockAlertNotificationsForBusiness(businessId);

      return { deleted, scanned: productsSnap.size };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', 'forBusiness', variables.businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', variables.businessId] });
      queryClient.invalidateQueries({ queryKey: ['productCategoryStats', variables.businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'productSales', variables.businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', variables.businessId] });
    },
  });
};

/** Applique le même seuil d’alerte stock à tous les produits d’un business (y compris inactifs). Super Admin uniquement. */
export const useSuperAdminApplyMinStockAlertToAllProducts = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();

  return useMutation({
    mutationFn: async (params: {
      businessId: string;
      minStockAlert: number;
      /** Si fourni (multi-sites), met à jour minStockAlertBySite pour chaque id, en fusionnant l’existant. */
      activeSiteIds?: string[];
    }) => {
      if (!isSuperAdmin) {
        throw new AppError('superAdminOnly');
      }
      const { businessId, activeSiteIds } = params;
      const threshold = Math.max(0, Math.floor(Number(params.minStockAlert)));

      const productsSnap = await getDocs(
        query(collection(db, COLLECTIONS.products), where('businessId', '==', businessId))
      ).catch((error) => {
        handleFirestoreError(error, 'useSuperAdminApplyMinStockAlertToAllProducts list');
        throw error;
      });

      let successCount = 0;
      let errorCount = 0;

      for (const d of productsSnap.docs) {
        try {
          if (activeSiteIds && activeSiteIds.length > 0) {
            const data = d.data();
            const existingBySite = (data.minStockAlertBySite || {}) as Record<string, number>;
            const nextBySite: Record<string, number> = { ...existingBySite };
            for (const siteId of activeSiteIds) {
              nextBySite[siteId] = threshold;
            }
            await updateDoc(doc(db, COLLECTIONS.products, d.id), {
              minStockAlert: threshold,
              minStockAlertBySite: nextBySite,
              updatedAt: Timestamp.now(),
            });
          } else {
            await updateDoc(doc(db, COLLECTIONS.products, d.id), {
              minStockAlert: threshold,
              updatedAt: Timestamp.now(),
            });
          }
          successCount += 1;
        } catch (error) {
          handleFirestoreError(error, `useSuperAdminApplyMinStockAlertToAllProducts update ${d.id}`);
          errorCount += 1;
        }
      }

      return { scanned: productsSnap.size, successCount, errorCount };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', 'forBusiness', variables.businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', variables.businessId] });
    },
  });
};